import {
  createSelector,
} from 'reselect';
import * as R from 'ramda';

import {
  getResourceMap,
  getResourceMappedAllList,
} from './resources';
import {
  capitalizeFirstLetter as cfl,
} from '../utils';


export const getFiltersType = (
  ({ filters }) => filters.filterType
);

// NOT MEMOIZED!!!
export const getAllFiltersIds = ({ filters: { filterType, ...rest } }) => rest;

export function getFiltersIds(filterType) {
  return state => state.filters[filterType];
}

const optionsSelectors = {};
const filterTypeSelectorConfig = {
  users: {
    type: 'simple',
    key: 'accountId',
    orKey: 'key',
  },
  projects: {
    type: 'simple',
    key: 'id',
  },
  epics: {
    type: 'simple',
    key: 'id',
  },
  worklogTypes: {
    type: 'simple',
    key: 'id',
  },
  issueTypes: {
    type: 'simple',
    key: 'id',
  },
  issueStatuses: {
    type: 'simple',
    key: 'id',
  },
  jqlFilters: {
    type: 'simple',
    key: 'id',
  },
  versions: {
    type: 'grouped',
    key: 'id',
    groupAttr: 'projectId',
    groupResource: 'projects',
  },
  components: {
    type: 'grouped',
    key: 'id',
    groupAttr: 'projectId',
    groupResource: 'projects',
  },
  sprints: {
    type: 'grouped',
    key: 'id',
    groupAttr: 'originBoardId',
    groupResource: 'boards',
  },
  boards: {
    type: 'grouped',
    key: 'id',
    groupAttr: 'type',
    groupResource: 'self',
  },
};

export function getFiltersOptions(filterType) {
  if (optionsSelectors[filterType]) {
    return optionsSelectors[filterType];
  }
  optionsSelectors[filterType] = (
    createSelector(
      [
        getFiltersIds(filterType),
        getResourceMappedAllList(
          filterType === 'epics' ? 'issues' : filterType,
        ),
        getResourceMap(
          filterType === 'epics' ? 'issues' : filterType,
        ),
        getResourceMap('projects'),
        getResourceMap('boards'),
        getResourceMap('sprints'),
      ],
      (
        filterIds,
        resourceMappedList,
        resourceMap,
        projectsMap,
        boardsMap,
        sprintsMap,
      ) => {
        const config = filterTypeSelectorConfig[filterType];
        const {
          key,
          orKey,
          type,
        } = config;
        if (type === 'simple') {
          return resourceMappedList
            .filter(fr => !filterIds.includes(fr[key] || fr[orKey]))
            .map(r => ({
              // $FlowFixMe
              label: filterType === 'epics' ? r.fields.summary : r.name,
              value: r[key] || r[orKey],
              meta: (() => {
                switch (filterType) {
                  case 'worklogTypes':
                    return {
                      type: 'wtype',
                      color: r.color,
                    };
                  default:
                    return null;
                }
              })(),
            }));
        }
        if (!config.groupAttr || !config.groupResource) {
          return [];
        }
        const {
          groupResource,
          groupAttr,
        } = config;
        const titleResourceMap = {
          projects: projectsMap,
          boards: boardsMap,
          sprints: sprintsMap,
          self: resourceMap,
        };
        const groupMap = titleResourceMap[groupResource];
        return [].concat(
          ...R.groupWith(
            (a, b) => a[groupAttr] === b[groupAttr],
            R.sortBy(
              R.prop(groupAttr),
              resourceMappedList.filter(fr => !filterIds.includes(fr[key] || fr[orKey])),
            ),
          ).map(
            g => ([
              {
                label: (
                  groupResource === 'self'
                    ? (
                      g[0][groupAttr]
                    )
                    : (
                      groupMap[g[0][groupAttr]]?.name
                    )
                ),
                value: g[0][groupAttr],
                disabled: true,
                meta: { type: 'title' },
              },
              ...g.map(r => ({
                label: r.name,
                value: r[key] || r[orKey],
              })),
            ]),
          ),
        );
      },
    )
  );
  return optionsSelectors[filterType];
}

export const getFilters = createSelector(
  [
    ({ filters }) => filters,
    getResourceMap('users'),
    getResourceMap('projects'),
    getResourceMap('boards'),
    getResourceMap('versions'),
    getResourceMap('issueTypes'),
    getResourceMap('issueStatuses'),
    getResourceMap('issues'),
    getResourceMap('sprints'),
    getResourceMap('worklogTypes'),
    getResourceMap('components'),
    getResourceMap('jqlFilters'),
  ],
  (
    filtersRaw,
    usersMap,
    projectsMap,
    boardsMap,
    versionsMap,
    issueTypesMap,
    issueStatusesMap,
    epicsMap,
    sprintsMap,
    worklogTypesMap,
    componentsMap,
    jqlFiltersMap,
  ) => {
    const {
      filterType: garbage,
      ...ids
    } = filtersRaw;
    const filterTypeMap = {
      users: usersMap,
      projects: projectsMap,
      boards: boardsMap,
      versions: versionsMap,
      issueTypes: issueTypesMap,
      issueStatuses: issueStatusesMap,
      epics: epicsMap,
      sprints: sprintsMap,
      worklogTypes: worklogTypesMap,
      components: componentsMap,
      jqlFilters: jqlFiltersMap,
    };
    return [].concat(...Object.entries(ids).map(([k, v]) => (
      v.map(id => ({
        option: {
          label: (() => {
            switch (k) {
              case 'users': {
                return filterTypeMap[k][id]?.displayName;
              }
              case 'epics':
                return filterTypeMap[k][id].fields.summary;
              default: {
                return filterTypeMap[k][id].name;
              }
            }
          })(),
          value: id,
        },
        selectedType: {
          label: (() => {
            switch (k) {
              case 'issueTypes':
                return 'Issue types';
              case 'issueStatuses':
                return 'Issue statuses';
              case 'jqlFilters':
                return 'Custom JQL Filters';
              default:
                return cfl(k);
            }
          })(),
          value: k,
        },
      })))));
  },
);
